import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { Rpfi05DayRow, Rpfi05TableResult } from '../../../../core/models/report/rpfi05.model';
import { MapperRpfi05Service } from '../../../../core/mappers/report/mapper-rpfi05.service';
import { ApiRpfi05Service } from '../../../../core/services/api/report/api-rpfi05.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { downloadReportExcel, buildReportExcelFilename } from '../../../../core/utils/report-excel-export.util';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalInvoicePdfViewerComponent } from '../../modals/finance/invoice/modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';

@Component({
  selector: 'app-rpfi05',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalInvoicePdfViewerComponent],
  templateUrl: './rpfi05.component.html',
  styleUrl: './rpfi05.component.css',
})
export class Rpfi05Component {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiRpfi05Service);
  private readonly mapper = inject(MapperRpfi05Service);
  private readonly toast = inject(ToastService);

  isLoading = signal(false);
  isExporting = signal(false);
  isProcessingPdf = signal(false);
  showPdfModal = signal(false);
  pdfSrc: Uint8Array | null = null;
  pdfDownloadName = 'RPFI05.pdf';

  startDate = '';
  endDate = '';
  lastFetchedTime: Date | null = null;
  /** true หลังกดค้นหาสำเร็จอย่างน้อยครั้งหนึ่ง (สำหรับ empty state) */
  hasSearched = signal(false);

  /** ช่วงวันที่สูงสุด 2 เดือน (calendar months) */
  private readonly maxRangeMonths = 2;

  result = signal<Rpfi05TableResult | null>(null);

  constructor() {
    const today = this.toInputDate(new Date());
    this.startDate = today;
    this.endDate = today;

    // เปลี่ยนลาน → เคลียร์ผลเดิม รอให้กดค้นหาเอง
    effect(() => {
      this.siteState.selectedSite()?.id;
      untracked(() => {
        this.clearResult();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canSearch(): boolean {
    return this.selectedSiteId > 0 && !!this.startDate && !!this.endDate;
  }

  get rows(): Rpfi05DayRow[] {
    return this.result()?.rows ?? [];
  }

  get totals(): Rpfi05DayRow | null {
    return this.result()?.totals ?? null;
  }

  get unmappedAmount(): number {
    return this.result()?.unmappedAmount ?? 0;
  }

  /** max ของวันสิ้นสุด = วันเริ่ม + 2 เดือน */
  get maxEndDate(): string | undefined {
    if (!this.startDate) return undefined;
    const start = this.parseInputDate(this.startDate);
    if (!start) return undefined;
    const max = new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate());
    return this.toInputDate(max);
  }

  onFilterChange(): void {
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
    if (this.startDate && this.endDate && this.isRangeOverMax(this.startDate, this.endDate)) {
      const maxEnd = this.maxEndDate;
      if (maxEnd) this.endDate = maxEnd;
      this.toast.warn('เรียกดูได้สูงสุด 2 เดือน — ปรับวันสิ้นสุดให้อัตโนมัติ');
    }
    // เปลี่ยนเงื่อนไขแล้ว — ไม่โหลดอัตโนมัติ แค่เคลียร์ผลเก่า
    this.clearResult();
  }

  onSearch(): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }
    if (this.startDate > this.endDate) {
      this.toast.error('วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }
    this.loadTable();
  }

  onRefresh(): void {
    if (!this.hasSearched()) {
      this.onSearch();
      return;
    }
    this.loadTable();
  }

  loadTable(): void {
    if (!this.canSearch) {
      this.clearResult();
      return;
    }

    this.isLoading.set(true);
    this.api.getRpfi05ForTable({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
    }).subscribe({
      next: (res) => {
        this.result.set(this.mapper.mapTable(res));
        this.hasSearched.set(true);
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: (err) => {
        this.result.set(null);
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.toast.error(err?.error?.message || 'โหลดรายงาน RPFI05 ไม่สำเร็จ');
      },
    });
  }

  private clearResult(): void {
    this.result.set(null);
    this.hasSearched.set(false);
    this.lastFetchedTime = null;
  }

  async onExportExcel(): Promise<void> {
    if (!this.result() || this.rows.length === 0) {
      this.toast.error('ไม่มีข้อมูลให้ส่งออก');
      return;
    }

    this.isExporting.set(true);
    try {
      const exportRows = [
        ...this.rows.map((r) => this.toExportRow(r, this.formatDate(r.paidDate))),
        this.toExportRow(this.totals!, 'Total'),
      ];
      const siteName = this.result()?.siteName || this.siteState.selectedSite()?.name || '—';
      await downloadReportExcel(exportRows, {
        reportTitle: 'RPFI05 — รายงานสรุปรายได้รายวัน',
        siteName,
        siteAddress: this.result()?.siteAddress,
        siteTaxId: this.result()?.siteTaxId,
        startDate: this.startDate,
        endDate: this.endDate,
        sheetName: 'RPFI05',
        fileName: buildReportExcelFilename('RPFI05', siteName, this.startDate, this.endDate),
      });
      this.toast.success('ส่งออก Excel สำเร็จ');
    } catch {
      this.toast.error('ส่งออก Excel ไม่สำเร็จ');
    } finally {
      this.isExporting.set(false);
    }
  }

  onPrintPdf(): void {
    if (!this.canSearch) {
      this.toast.error('กรุณาเลือกลานและช่วงวันที่');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูรายงานได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }

    this.isProcessingPdf.set(true);
    const siteName = (this.siteState.selectedSite()?.name || 'site').replace(/\s+/g, '_');
    this.pdfDownloadName = `RPFI05_${siteName}_${this.startDate}_${this.endDate}.pdf`;

    this.api.getRpfi05Pdf({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
    }).subscribe({
      next: (bytes) => {
        this.pdfSrc = bytes;
        this.showPdfModal.set(true);
        this.isProcessingPdf.set(false);
      },
      error: (err) => {
        console.error('[API Error] getRpfi05Pdf:', err);
        this.toast.error('ไม่สามารถดึงไฟล์ PDF ได้');
        this.isProcessingPdf.set(false);
      },
    });
  }

  closePdfModal(): void {
    this.showPdfModal.set(false);
    this.pdfSrc = null;
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  private toExportRow(r: Rpfi05DayRow, dateLabel: string): Record<string, string | number> {
    return {
      'วันที่': dateLabel,
      'EV ค่าชาร์จ': r.evCharging,
      'รถ จอดรถ': r.carParking,
      'รถ สมาชิก': r.carMember,
      'รถ บัตรหาย': r.carLostCard,
      'รถ รวม': r.carTotal,
      'มอไซค์ จอดรถ': r.mcParking,
      'มอไซค์ สมาชิก': r.mcMember,
      'มอไซค์ บัตรหาย': r.mcLostCard,
      'มอไซค์ รวม': r.mcTotal,
      'รวม จอดรถ': r.totalParking,
      'รวม สมาชิก': r.totalMember,
      'รวม บัตรหาย': r.totalLostCard,
      'ค่าบัตรส่วนลด': r.discountCard,
      'รายได้ E-stamp': r.estamp,
      'คูปองค่าบริการ': r.coupon,
      'ยอดรวมทั้งสิ้น': r.grandTotal,
    };
  }

  private toInputDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseInputDate(value: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  /** true ถ้า end > start + 2 เดือน */
  private isRangeOverMax(startStr: string, endStr: string): boolean {
    const start = this.parseInputDate(startStr);
    const end = this.parseInputDate(endStr);
    if (!start || !end) return false;
    const maxEnd = new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate());
    return end > maxEnd;
  }
}
